import React, { useState } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { Plus, X, Stethoscope, Star, PencilSimple, Check } from '@phosphor-icons/react';

const ROLE_OPTIONS = [
  'GP', 'Cardiologist', 'Psychiatrist', 'Psychologist', 'Endocrinologist',
  'Neurologist', 'Rheumatologist', 'Dermatologist', 'Oncologist',
  'Gastroenterologist', 'Pulmonologist', 'Nephrologist', 'Urologist',
  'Physiotherapist', 'Dietitian', 'Pharmacist', 'Dentist', 'Surgeon', 'Other',
];

const EMPTY = { name: '', role: '', specialty: '', clinic_name: '', phone: '', is_primary: false, linked_conditions: [], notes: '' };

// Simple heuristic: which conditions might a specialty relate to
const SPECIALTY_HINTS = {
  'Cardiologist': ['heart', 'cardiac', 'atrial', 'hypertension', 'blood pressure', 'cholesterol', 'myocarditis', 'arrhythmia'],
  'Endocrinologist': ['diabetes', 'thyroid', 'hormone', 'pcos', 'addison', 'cushing'],
  'Psychiatrist': ['bipolar', 'depression', 'anxiety', 'schizophrenia', 'ptsd', 'ocd', 'adhd', 'borderline'],
  'Psychologist': ['bipolar', 'depression', 'anxiety', 'ptsd', 'ocd', 'adhd', 'eating', 'trauma'],
  'Neurologist': ['epilepsy', 'migraine', 'parkinson', 'multiple sclerosis', 'neuropathy', 'seizure'],
  'Rheumatologist': ['arthritis', 'lupus', 'fibromyalgia', 'gout', 'autoimmune'],
  'Gastroenterologist': ['crohn', 'colitis', 'ibs', 'celiac', 'reflux', 'liver', 'hepatitis'],
  'Pulmonologist': ['asthma', 'copd', 'sleep apnea', 'lung', 'pulmonary'],
  'Oncologist': ['cancer', 'tumor', 'lymphoma', 'leukemia', 'melanoma'],
  'Nephrologist': ['kidney', 'renal', 'dialysis'],
  'Dermatologist': ['eczema', 'psoriasis', 'acne', 'skin'],
};

const suggestConditions = (role, conditions) => {
  const hints = SPECIALTY_HINTS[role];
  if (!hints || !conditions.length) return [];
  return conditions
    .filter(c => hints.some(h => c.name.toLowerCase().includes(h)))
    .map(c => c.name);
};

export const OnboardingMedicalTeam = ({ data, onChange }) => {
  const team = data.medical_team || [];
  const conditions = data.conditions || [];
  const [draft, setDraft] = useState({ ...EMPTY });
  const [showForm, setShowForm] = useState(false);
  const [editingIdx, setEditingIdx] = useState(null);
  const [suggestions, setSuggestions] = useState([]);

  const addMember = () => {
    if (!draft.name.trim()) return;
    const cleaned = { ...draft, name: draft.name.trim(), clinic_name: draft.clinic_name.trim() };
    if (editingIdx !== null) {
      const updated = [...team];
      updated[editingIdx] = cleaned;
      onChange({ ...data, medical_team: updated });
      setEditingIdx(null);
    } else {
      onChange({ ...data, medical_team: [...team, cleaned] });
    }
    setDraft({ ...EMPTY });
    setShowForm(false);
    setSuggestions([]);
  };

  const removeMember = (idx) => {
    onChange({ ...data, medical_team: team.filter((_, i) => i !== idx) });
  };

  const startEdit = (idx) => {
    setDraft({ ...team[idx] });
    setEditingIdx(idx);
    setShowForm(true);
  };

  const handleRoleChange = (role) => {
    setDraft(d => ({ ...d, role }));
    // Check for linking suggestions
    const matched = suggestConditions(role, conditions);
    const alreadyLinked = draft.linked_conditions || [];
    const newSuggestions = matched.filter(c => !alreadyLinked.includes(c));
    setSuggestions(newSuggestions);
  };

  const acceptSuggestion = (condName) => {
    setDraft(d => ({ ...d, linked_conditions: [...(d.linked_conditions || []), condName] }));
    setSuggestions(s => s.filter(c => c !== condName));
  };

  const toggleCondition = (condName) => {
    setDraft(d => ({
      ...d,
      linked_conditions: (d.linked_conditions || []).includes(condName)
        ? d.linked_conditions.filter(c => c !== condName)
        : [...(d.linked_conditions || []), condName]
    }));
  };

  return (
    <motion.div initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }} className="space-y-6">
      <div className="bg-white border border-slate-150 rounded-2xl p-5">
        <p className="text-slate-700 leading-relaxed">
          Who's on your care team?
        </p>
        <p className="text-sm text-slate-400 mt-2">
          Your GP, specialists, allied health — anyone involved in your care.
        </p>
      </div>

      {/* Added members */}
      <AnimatePresence>
        {team.length > 0 && (
          <motion.div initial={{ opacity: 0 }} animate={{ opacity: 1 }} className="space-y-2.5">
            {team.map((m, idx) => (
              <motion.div key={idx} initial={{ opacity: 0, scale: 0.95 }} animate={{ opacity: 1, scale: 1 }}
                exit={{ opacity: 0, scale: 0.95 }}
                className="bg-white border border-slate-200 rounded-xl p-4 flex items-start justify-between"
                data-testid="onboarding-team-item">
                <div className="flex items-start gap-3">
                  <div className={`w-8 h-8 rounded-full flex items-center justify-center flex-shrink-0 ${
                    m.is_primary ? 'bg-[#235347]/10 text-[#235347]' : 'bg-slate-100 text-slate-400'
                  }`}>
                    <Stethoscope size={16} weight={m.is_primary ? 'fill' : 'regular'} />
                  </div>
                  <div>
                    <div className="flex items-center gap-1.5">
                      <p className="text-sm font-medium text-slate-900">{m.name}</p>
                      {m.is_primary && <Star size={12} weight="fill" className="text-amber-500" />}
                    </div>
                    <p className="text-xs text-slate-500">{m.role}{m.specialty ? ` · ${m.specialty}` : ''}</p>
                    {m.clinic_name && <p className="text-[10px] text-slate-400">{m.clinic_name}</p>}
                    {m.linked_conditions?.length > 0 && (
                      <div className="flex flex-wrap gap-1 mt-1">
                        {m.linked_conditions.map(c => (
                          <span key={c} className="text-[9px] px-1.5 py-0.5 bg-[#235347]/8 text-[#235347] rounded">{c}</span>
                        ))}
                      </div>
                    )}
                  </div>
                </div>
                <div className="flex items-center gap-1 flex-shrink-0">
                  <button onClick={() => startEdit(idx)} className="text-slate-300 hover:text-[#235347] p-1" data-testid={`edit-team-${idx}`}>
                    <PencilSimple size={14} />
                  </button>
                  <button onClick={() => removeMember(idx)} className="text-slate-300 hover:text-red-500 p-1" data-testid={`remove-team-${idx}`}>
                    <X size={14} />
                  </button>
                </div>
              </motion.div>
            ))}
          </motion.div>
        )}
      </AnimatePresence>

      {/* Add / Edit form */}
      {showForm ? (
        <div className="bg-slate-50 border border-slate-200 rounded-xl p-4 space-y-3" data-testid="onboarding-team-form">
          <input value={draft.name} onChange={(e) => setDraft(d => ({ ...d, name: e.target.value }))}
            placeholder="Clinician name" autoFocus data-testid="onboarding-team-name"
            className="w-full bg-white border border-slate-200 rounded-lg px-4 py-2.5 text-sm outline-none focus:ring-2 focus:ring-[#235347]/20" />

          <div className="grid grid-cols-2 gap-2">
            <select value={draft.role} onChange={(e) => handleRoleChange(e.target.value)}
              data-testid="onboarding-team-role"
              className="bg-white border border-slate-200 rounded-lg px-3 py-2.5 text-sm outline-none">
              <option value="">Role...</option>
              {ROLE_OPTIONS.map(r => <option key={r} value={r}>{r}</option>)}
            </select>
            <input value={draft.specialty} onChange={(e) => setDraft(d => ({ ...d, specialty: e.target.value }))}
              placeholder="Specialty (optional)" data-testid="onboarding-team-specialty"
              className="bg-white border border-slate-200 rounded-lg px-3 py-2.5 text-sm outline-none" />
          </div>

          <input value={draft.clinic_name} onChange={(e) => setDraft(d => ({ ...d, clinic_name: e.target.value }))}
            placeholder="Clinic or practice name (optional)"
            className="w-full bg-white border border-slate-200 rounded-lg px-4 py-2.5 text-sm outline-none" />

          <input value={draft.phone} onChange={(e) => setDraft(d => ({ ...d, phone: e.target.value }))}
            placeholder="Phone (optional)"
            className="w-full bg-white border border-slate-200 rounded-lg px-4 py-2.5 text-sm outline-none" />

          <label className="flex items-center gap-2 text-sm text-slate-600 cursor-pointer">
            <input type="checkbox" checked={draft.is_primary}
              onChange={(e) => setDraft(d => ({ ...d, is_primary: e.target.checked }))}
              className="rounded border-slate-300 text-[#235347] focus:ring-[#235347]" />
            Primary clinician for this type
          </label>

          {/* Linking suggestions (Item 9) */}
          <AnimatePresence>
            {suggestions.length > 0 && (
              <motion.div initial={{ opacity: 0, height: 0 }} animate={{ opacity: 1, height: 'auto' }}
                exit={{ opacity: 0, height: 0 }} className="overflow-hidden">
                <div className="bg-[#235347]/5 rounded-lg p-3" data-testid="team-link-suggestion">
                  <p className="text-xs text-[#235347] font-medium mb-1.5">
                    Link to your conditions?
                  </p>
                  <div className="flex flex-wrap gap-1.5">
                    {suggestions.map(c => (
                      <button key={c} onClick={() => acceptSuggestion(c)}
                        className="text-xs px-2.5 py-1 bg-white border border-[#235347]/20 text-[#235347] rounded-full hover:bg-[#235347]/10 transition-colors"
                        data-testid={`suggest-link-${c}`}>
                        + {c}
                      </button>
                    ))}
                  </div>
                </div>
              </motion.div>
            )}
          </AnimatePresence>

          {/* Manual condition linking */}
          {conditions.length > 0 && (
            <div>
              <label className="text-[10px] text-slate-400 font-medium mb-1.5 block">Manages conditions</label>
              <div className="flex flex-wrap gap-1.5">
                {conditions.map(c => {
                  const selected = (draft.linked_conditions || []).includes(c.name);
                  return (
                    <button key={c.name} onClick={() => toggleCondition(c.name)}
                      className={`text-xs px-2.5 py-1 rounded-full border transition-colors ${
                        selected ? 'bg-[#235347] text-white border-[#235347]' : 'bg-white text-slate-500 border-slate-200'
                      }`}>
                      {selected && <Check size={10} weight="bold" className="inline mr-0.5" />}
                      {c.name}
                    </button>
                  );
                })}
              </div>
            </div>
          )}

          <div className="flex gap-2">
            <button onClick={() => { setShowForm(false); setEditingIdx(null); setDraft({ ...EMPTY }); setSuggestions([]); }}
              className="flex-1 py-2.5 text-sm text-slate-500 border border-slate-200 rounded-lg hover:bg-white transition-colors">
              Cancel
            </button>
            <button onClick={addMember} disabled={!draft.name.trim()} data-testid="onboarding-team-save"
              className="flex-1 py-2.5 bg-[#235347] text-white rounded-lg text-sm font-medium disabled:opacity-40 hover:bg-[#1A3F36] transition-colors">
              {editingIdx !== null ? 'Update' : 'Add'}
            </button>
          </div>
        </div>
      ) : (
        <button onClick={() => setShowForm(true)} data-testid="onboarding-team-add-btn"
          className="w-full py-3 border-2 border-dashed border-slate-200 rounded-xl text-sm text-slate-500 hover:border-[#235347] hover:text-[#235347] transition-colors flex items-center justify-center gap-2">
          <Plus size={16} /> {team.length > 0 ? 'Add another clinician' : 'Add a clinician'}
        </button>
      )}
    </motion.div>
  );
};
